import React from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/16/solid';
import Utilities from '@util/Utilities';

// A dropdown with one checkbox per option and a limit on the count of ticked options.
// The caller owns the selection: onToggle receives the value that the user clicked.
// An option is disabled when the limit is reached (max) or when it is the last one (min).
// variant 'link' (default): a text button, for a table header. variant 'button': a white toolbar button with a ring,
// the look of the MonthRangePicker button, so that the two sit side by side in a card header.
export type CheckboxMenuOption<T extends string | number> = {
	value: T,
	label: string,
};

type Props<T extends string | number> = {
	label: React.ReactNode,
	options: CheckboxMenuOption<T>[],
	selected: T[],
	onToggle: (value: T) => void,
	max?: number,
	min?: number,
	hint?: string,
	title?: string,
	anchor?: 'bottom start' | 'bottom end' | 'bottom',
	buttonClassName?: string,
	variant?: 'link' | 'button',
};

const CheckboxMenu = <T extends string | number,>({
	label,
	options,
	selected,
	onToggle,
	max,
	min = 1,
	hint,
	title,
	anchor = 'bottom start',
	buttonClassName,
	variant = 'link',
}: Props<T>) => {
	const isButton = variant === 'button';
	return <Popover className="relative inline-block">
		<PopoverButton
			title={title}
			className={Utilities.classNames(
				isButton
					? 'inline-flex h-9 items-center gap-x-2 whitespace-nowrap rounded-md bg-white px-3 text-sm font-medium text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600'
					: 'inline-flex items-center gap-x-1 whitespace-nowrap rounded hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500',
				buttonClassName ?? '')}>
			{label}
			<ChevronDownIcon className={isButton ? '-mr-1 h-4 w-4 text-gray-400' : 'h-3 w-3'} aria-hidden="true" />
		</PopoverButton>
		<PopoverPanel anchor={isButton ? { to: anchor, gap: 8 } : anchor}
			className={Utilities.classNames('z-50 rounded-md bg-white py-1 text-left shadow-lg ring-1 ring-black ring-opacity-5', isButton ? 'w-56' : 'mt-1 w-40')}>
			{options.map(option => {
				const checked = selected.includes(option.value);
				const disabled = (!checked && max !== undefined && selected.length >= max) || (checked && selected.length <= min);
				return <label key={String(option.value)}
											className={Utilities.classNames(
												'flex items-center gap-x-2 px-3 py-2 text-sm font-normal',
												disabled ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer text-gray-700 hover:bg-gray-100',
												checked ? 'bg-gray-50' : '')}>
					<input type="checkbox"
									checked={checked}
									disabled={disabled}
									onChange={() => onToggle(option.value)}
									className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
					{option.label}
				</label>;
			})}
			{hint !== undefined && hint !== '' &&
				<p className="border-t border-gray-100 px-3 pb-1 pt-1.5 text-xs font-normal text-gray-500">{hint}</p>}
		</PopoverPanel>
	</Popover>;
};

export default CheckboxMenu;
